import {
  DeferredCodeBlock as CodeBlock,
  frameworkCreateProjectCodeSnippet,
  frameworkLabels,
} from "@prismicio/editor-fields"
import { Box, Button, Text } from "@prismicio/editor-ui"

import { getRepositoryName } from "../app/routing"
import { useRepository } from "../app/services/repository"
import { useSharedOnboarding } from "../app/services/sharedOnboarding"
import { trackPbOnboardingStep } from "../app/services/tracking"
import { usePrefetchDefaultLocalesQuery } from "../settings/locales/useDefaultLocales"
import { usePrefetchRepositoryLocalesQuery } from "../settings/locales/useRepositoryLocales"
import {
  getIsSupportedFramework,
  UnsupportedFrameworkStep,
  useRevalidateCustomTypes,
} from "./OnboardingSteps"

/**
 * This is temporary step for the onboarding drop off experiment.
 * We will remove this once the experiment is over or convert it with proper design system components.
 */
export function UpfrontStep() {
  const { revalidate, isRevalidating } = useRevalidateCustomTypes()
  const { completeStep } = useSharedOnboarding()
  const repositoryName = getRepositoryName()
  const { framework } = useRepository()
  const isSupportedFramework = getIsSupportedFramework(framework)

  usePrefetchDefaultLocalesQuery()
  usePrefetchRepositoryLocalesQuery()

  // if the user has selected a unsupported framework we allow them to go straight to the custom types builder
  if (!isSupportedFramework) {
    return <UnsupportedFrameworkStep shouldAnimate={false} />
  }

  const label = frameworkLabels[framework]
  const createProjectCodeSnippet = frameworkCreateProjectCodeSnippet[framework]

  const onContinue = () => {
    void revalidate()

    void completeStep("chooseLocale")
    void completeStep("createProject")
    void completeStep("setupSliceMachine")

    trackPbOnboardingStep("pushModels")
  }

  const steps = [
    {
      number: "01",
      title: "Create your project",
      description: `Use this command to scaffold a new ${label} app locally.\nIf you already have one, feel free to skip this step.`,
      content: (
        <CodeBlock
          title="Terminal"
          code={`${createProjectCodeSnippet} ${repositoryName}`}
          language="bash"
        />
      ),
    },
    {
      number: "02",
      title: "Install the Prismic Dev Tool",
      description:
        "This installs the Prismic Dev Tool (Slice Machine) and prepares your project locally so you can start creating custom types. It's safe to run — nothing gets overwritten.",
      content: (
        <CodeBlock
          title="Terminal"
          code={`cd ${repositoryName}
npx @slicemachine/init@latest --repository ${repositoryName}`}
          language="bash"
        />
      ),
    },
    {
      number: "03",
      title: "Start the Prismic Dev Tool",
      description:
        "Once installed, start the Prismic Dev Tool by running the following command:",
      content: (
        <CodeBlock
          title="Terminal"
          code="npm run slicemachine"
          language="bash"
        />
      ),
    },
    {
      number: "04",
      title: "Push your custom types",
      description:
        "Create your custom types using the Prismic Dev Tool.\nClick below when you're ready to continue.",
      content: (
        <Button
          endIcon="arrowForward"
          size="large"
          onClick={onContinue}
          loading={isRevalidating}
        >
          I've pushed my custom types
        </Button>
      ),
    },
  ]

  return (
    <Box
      flexDirection="column"
      padding={{ inline: 16 }}
      margin={{ top: 60, inline: "auto" }}
      width="640px"
    >
      <Text variant="bold" color="purple10" sx={{ marginBottom: 8 }}>
        Let's build it
      </Text>

      <Text variant="h2" sx={{ marginBottom: 8 }}>
        Set up your project
      </Text>

      <Text variant="normal" color="grey11">
        Define the custom types your website will use. These steps install the
        Prismic Dev Tool and set everything up locally — right from your
        codebase.
      </Text>

      <Box flexDirection="column" margin={{ top: 32 }} padding={{ top: 16 }}>
        {steps.map((step, index) => (
          <Box flexDirection="row" key={step.number}>
            <Box
              flexDirection="column"
              position="relative"
              width="32px"
              margin={{ right: 16 }}
            >
              <Box
                as="div"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="32px"
                height="32px"
                backgroundColor="grey1"
                borderRadius={4}
                borderColor="grey7"
                border
              >
                <Text variant="small" color="grey11">
                  {step.number}
                </Text>
              </Box>
              <Box
                position="absolute"
                top="32px"
                left="17px"
                width="0px"
                height={index < steps.length - 1 ? "calc(100% - 32px)" : "8px"}
                border={{ left: true }}
                borderStyle="dashed"
                borderColor="grey6"
              />
            </Box>
            <Box flexDirection="column" flexGrow={1}>
              <Text variant="bold" sx={{ marginBottom: 8 }}>
                {step.title}
              </Text>

              <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
                {step.description}
              </Text>

              <Box margin={{ bottom: 32 }} display="block">
                {step.content}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
